
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ModalService } from '../../services/modal.service';
import { ToastService } from '../../services/toast.service';
import { MachineApiService } from '../../services/machine-api.service';
import { IntegrationsService } from '../../services/integrations.service';
import {
  AGENT_MODEL_OPTIONS,
  DEFAULT_AGENT_MODEL_ID,
  chatSurfaceKeyFromPage,
  isKnownAgentModelId,
  normalizeAgentModelId,
} from '../../services/agent-models';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { PromptAnalyserComponent } from '../prompt-analyser/prompt-analyser.component';

type ChatMessage = { role: 'user' | 'assistant'; text: string };
export type AiOrchestratorResult = { page: string; message: string; patches: any[]; raw?: any };

@Component({
  selector: 'app-chat',
  imports: [NgbTooltip, PromptAnalyserComponent],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss'
})
export class ChatComponent implements OnInit, AfterViewInit {
  @Input() placeholder: string = '';
  @Input() disabled = false;
  private _conversationId: string | null = null;
  @Input() set conversationId(value: string | null) {
    const next = value ?? null;
    const prev = this._conversationId;
    this._conversationId = next;
    if (prev === null) return; // first set
    if (prev === next) return;
    this.resetConversation();
  }
  get conversationId(): string | null {
    return this._conversationId;
  }
  private promptInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('promptInput') set promptInputRef(value: ElementRef<HTMLTextAreaElement> | undefined) {
    this.promptInput = value;
    if (!value) return;
    queueMicrotask(() => {
      this.focusPromptInput();
      this.adjustPromptInputHeight(value.nativeElement);
    });
  }
  @ViewChild('messagesContainer') messagesContainer?: ElementRef<HTMLDivElement>;

  @Input() suggestions: any[] = [];
  @Input() context: any = {};

  /**
   * Optional page-specific state to include with orchestrator calls.
   * Example: `{ definition: currentDraft }`
   */
  @Input() orchestratorPageContext: any = null;
  @Input() modelSurfaceKey: string | null = null;

  /**
   * Optional messages provided by the parent (loaded from DB).
   * When set, they will be used to seed the conversation on init.
   */
  private _initialMessages: ChatMessage[] | null = null;
  private lastHydratedConversationId: string | null = null;
  private staleInitialMessagesRef: ChatMessage[] | null = null;
  @Input() set initialMessages(value: ChatMessage[] | null) {
    this._initialMessages = value;

    // Deterministic hydration:
    // - Never hydrate with the stale reference from the previous conversation.
    // - When the conversationId changes, hydrate from the first non-stale value provided
    //   by the parent, even if it's empty.
    const cid = this._conversationId ?? null;
    if (cid !== this.lastHydratedConversationId) {
      if (value === this.staleInitialMessagesRef) return;
      this.messages = Array.isArray(value) ? [...value] : [];
      this.lastHydratedConversationId = cid;
      this.staleInitialMessagesRef = null;
      queueMicrotask(() => this.scrollToBottom());
      return;
    }

    // Normal case (same conversation): only hydrate if we currently have no messages
    // (covers late-arriving data on refresh).
    if (this.messages.length === 0 && Array.isArray(value) && value.length > 0) {
      this.messages = [...value];
      queueMicrotask(() => this.scrollToBottom());
    }
  }
  get initialMessages(): ChatMessage[] | null {
    return this._initialMessages;
  }

  /**
   * Emits whenever the message list changes (send/receive/clear).
   */
  @Output() messagesChange = new EventEmitter<ChatMessage[]>();

  /**
   * Emits each time a new message is added.
   */
  @Output() messageCreated = new EventEmitter<ChatMessage>();

  /**
   * Emits when an orchestrator response completes and can be parsed.
   */
  @Output() aiResult = new EventEmitter<AiOrchestratorResult>();

  messages: ChatMessage[] = [];
  currentPrompt: string = '';
  public isPromptExpanded = false;

  get hasConversation(): boolean {
    return this.messages.length > 0;
  }

  private inFlightAbort?: AbortController;
  public isStreaming = false;
  private isListening: boolean = false;
  private attachedFiles: File[] = [];
  public selectedModelId: string = DEFAULT_AGENT_MODEL_ID;
  public lastRequestedModelId: string = '';
  public lastUsedModelId: string = '';
  public lastUsedModelWasFallback = false;
  public readonly modelOptions = AGENT_MODEL_OPTIONS;
  private modelSelectionTouched = false;
  public modelSelectionLoading = false;
  private modelSelectionReadyPromise: Promise<void> | null = null;

  constructor(
    private modalService: ModalService,
    private toast: ToastService,
    private machineApi: MachineApiService,
    private integrations: IntegrationsService
  ) {}

  public ngOnInit(): void {
    // Seeding is handled by the `initialMessages` input setter (supports late-arriving data).
    this.modelSelectionReadyPromise = this.loadConfiguredDefaultModel();
  }

  public ngAfterViewInit() {
    this.focusPromptInput();
  }

  private resetConversation(): void {
    // Stop any in-flight generation for the previous conversation.
    try {
      this.inFlightAbort?.abort();
    } catch {}
    this.inFlightAbort = undefined;
    this.isStreaming = false;
    this.isListening = false;
    this.attachedFiles = [];
    this.currentPrompt = '';
    try {
      if (this.promptInput?.nativeElement) this.promptInput.nativeElement.value = '';
    } catch {}
    this.resetPromptInputHeight();

    // Prevent any stale parent-provided messages from rehydrating during the switch window.
    // Keep a reference to what we had so we can ignore it if it comes through again.
    this.staleInitialMessagesRef = this._initialMessages;
    this._initialMessages = null;
    this.lastHydratedConversationId = null;

    // Clear messages immediately so we don't show the previous project's chat.
    this.messages = [];
    // IMPORTANT: do not emit messagesChange here; project switches must not persist/overwrite chat.
  }

  public onSuggestionClick(suggestion: any) {
    if (this.disabled || this.isStreaming) return;
    if (!this.promptInput?.nativeElement) return;
    this.promptInput.nativeElement.value = suggestion.prompt;
    this.currentPrompt = suggestion.prompt;
    this.adjustPromptInputHeight();
    this.focusPromptInput();
  }

  public onSend() {
    if (this.disabled) return;
    const value = (this.promptInput?.nativeElement.value || '').trim();
    if (!value) return;
    void this.sendPrompt(value);
  }

  public onComposerKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return;
    if (event.shiftKey) return;
    if (event.isComposing || event.keyCode === 229) return;
    event.preventDefault();
    this.onSend();
  }

  public sendInjectedPrompt(text: string) {
    if (this.disabled) return;
    const value = String(text ?? '').trim();
    if (!value) return;
    // Use the same flow as normal sending.
    void this.sendPrompt(value);
  }

  private pageForOrchestrator(): string {
    const fromCtx = typeof this.context === 'object' && this.context ? String(this.context.page ?? '') : '';
    return (fromCtx || 'default').trim() || 'default';
  }

  public onModelChange(value: string) {
    this.setModelSelection(value);
  }

  public setModelSelection(value: string): void {
    const next = this.normalizeModelId(String(value ?? '').trim());
    if (!next) return;
    if (!this.modelOptions.some((m) => this.normalizeModelId(m.id) === next)) return;
    this.selectedModelId = next;
    this.modelSelectionTouched = true;
    this.logModelSelectionState('manual-change', next);
  }

  private modelLabelById(id: string): string {
    const normalized = this.normalizeModelId(id);
    const option = this.modelOptions.find((m) => this.normalizeModelId(m.id) === normalized);
    return option?.label ?? normalized;
  }

  get lastModelIndicator(): string {
    if (!this.lastUsedModelId) return '';
    const used = this.modelLabelById(this.lastUsedModelId);
    if (!this.lastUsedModelWasFallback) return `Model used: ${used}`;
    const requested = this.lastRequestedModelId ? this.modelLabelById(this.lastRequestedModelId) : '';
    if (requested && requested !== used) return `Model used: ${used} (fallback from ${requested})`;
    return `Model used: ${used} (fallback)`;
  }

  private normalizeModelId(id: string | null | undefined): string {
    return normalizeAgentModelId(id);
  }

  private currentSurfaceKey(): string {
    const explicit = String(this.modelSurfaceKey ?? '').trim();
    if (explicit) return explicit;
    return chatSurfaceKeyFromPage(this.pageForOrchestrator());
  }

  private surfaceRouteHint(): 'chat' | 'tickets' {
    return this.pageForOrchestrator().toLowerCase() === 'tickets' ? 'tickets' : 'chat';
  }

  private async loadConfiguredDefaultModel(): Promise<void> {
    this.modelSelectionLoading = true;
    try {
      const snapshot = await this.integrations.getSnapshot();
      if (this.modelSelectionTouched) return;
      const prefs = snapshot?.preferences ?? {};
      const surfaceKey = this.currentSurfaceKey();
      const routeHint = this.surfaceRouteHint();
      const surfaceDefaults =
        prefs?.surfaceModelDefaults && typeof prefs.surfaceModelDefaults === 'object'
          ? prefs.surfaceModelDefaults
          : {};
      const configured =
        this.normalizeModelId(surfaceDefaults?.[surfaceKey]) ||
        (routeHint === 'tickets' ? this.normalizeModelId(prefs?.routeModelDefaultTickets) : '') ||
        (routeHint === 'chat' ? this.normalizeModelId(prefs?.routeModelDefaultChat) : '') ||
        this.normalizeModelId(prefs?.defaultModel);
      if (configured && isKnownAgentModelId(configured)) {
        this.selectedModelId = configured;
        this.logModelSelectionState('settings-default', configured);
        return;
      }
      this.logModelSelectionState('built-in-default', this.selectedModelId);
    } catch {
      // Keep the built-in default if settings are unavailable.
      this.logModelSelectionState('settings-unavailable', this.selectedModelId);
    } finally {
      this.modelSelectionLoading = false;
    }
  }

  private async ensureModelSelectionReady(): Promise<void> {
    try {
      await this.modelSelectionReadyPromise;
    } finally {
      this.modelSelectionReadyPromise = null;
    }
  }

  private isRateLimitError(status: number | null, errText: string): boolean {
    if (status === 429) return true;
    const t = String(errText ?? '').toLowerCase();
    return (
      t.includes('resource_exhausted') ||
      t.includes('rate limit') ||
      t.includes('quota') ||
      t.includes('too many requests') ||
      t.includes('429')
    );
  }

  private modelTryOrder(): string[] {
    const first = this.normalizeModelId(this.selectedModelId);
    if (!first) return [];

    const providerOf = (id: string) => {
      const v = this.normalizeModelId(id);
      const idx = v.indexOf(':');
      if (idx <= 0) return 'google-genai';
      return v.slice(0, idx);
    };

    const provider = providerOf(first);
    const rest = this.modelOptions
      .map((m) => this.normalizeModelId(m.id))
      .filter((id) => id && id !== first && providerOf(id) === provider);

    return [first, ...rest];
  }

  private isChatParseDebugEnabled(): boolean {
    try {
      // localStorage.setItem('debugChatParse', '1')
      // localStorage.setItem('debugChatParse', '0')
      return localStorage.getItem('debugChatParse') === '1';
    } catch {
      return false;
    }
  }

  private isLightweightGreeting(text: string): boolean {
    const t = String(text ?? '').trim().toLowerCase();
    if (!t) return false;
    return ['hi', 'hello', 'hey', 'yo', 'sup', 'thanks', 'thank you'].includes(t);
  }

  private logAgentEvent(
    event: 'request' | 'resolved' | 'error',
    payload: {
      page: string;
      requestedModel?: string;
      usedModel?: string;
      usedFallback?: boolean;
      promptPreview?: string;
      error?: string;
    }
  ): void {
    const requested = this.normalizeModelId(payload.requestedModel);
    const used = this.normalizeModelId(payload.usedModel);
    const requestedLabel = requested ? this.modelLabelById(requested) : '';
    const usedLabel = used ? this.modelLabelById(used) : '';
    const preview = String(payload.promptPreview ?? '').trim();
    const previewTrimmed = preview.length > 120 ? `${preview.slice(0, 120)}...` : preview;
  }

  private logModelSelectionState(source: 'settings-default' | 'built-in-default' | 'settings-unavailable' | 'manual-change', modelId: string): void {
    const normalized = this.normalizeModelId(modelId);
  }

  private async sendPrompt(text: string): Promise<void> {
    if (this.disabled) return;
    await this.ensureModelSelectionReady();
    if (this.isStreaming) {
      this.toast.warning('Please stop current response first');
      return;
    }
    const promptText = String(text ?? '').trim();
    if (!promptText) {
      this.toast.warning('Please enter a message first');
      return;
    }

    // Push user message
    const userMsg: ChatMessage = { role: 'user', text: promptText };
    this.messages.push(userMsg);
    this.messageCreated.emit(userMsg);
    if (this.promptInput?.nativeElement) this.promptInput.nativeElement.value = '';
    this.currentPrompt = '';
    this.resetPromptInputHeight();
    this.scrollToBottom();

    // Create assistant placeholder message we will stream into.
    const assistantMsg: ChatMessage = { role: 'assistant', text: 'Generating…' };
    this.messages.push(assistantMsg);
    this.scrollToBottom();

    if (this.pageForOrchestrator() !== 'global' && this.isLightweightGreeting(promptText)) {
      assistantMsg.text = 'Hello! How can I help?';
      this.messageCreated.emit(assistantMsg);
      this.messagesChange.emit(this.messages);
      this.scrollToBottom();
      return;
    }

    const page = this.pageForOrchestrator();
    const baseBody = {
      page,
      conversationId: this.conversationId ?? undefined,
      content: [{ type: 'text', text: promptText }],
      pageContext: this.orchestratorPageContext ?? null,
    } as any;

    const abort = new AbortController();
    this.inFlightAbort = abort;
    this.isStreaming = true;
    const requestedModelId = this.normalizeModelId(this.selectedModelId);
    this.lastRequestedModelId = requestedModelId;
    this.lastUsedModelId = '';
    this.lastUsedModelWasFallback = false;
    this.logAgentEvent('request', {
      page,
      requestedModel: requestedModelId,
      promptPreview: promptText,
    });

    try {
      let responseModelId = '';
      let resolutionLogged = false;
      const logResolutionIfNeeded = () => {
        if (resolutionLogged) return;
        if (!responseModelId) return;
        const usedFallback = this.normalizeModelId(responseModelId) !== this.normalizeModelId(requestedModelId);
        this.logAgentEvent('resolved', {
          page,
          requestedModel: requestedModelId,
          usedModel: responseModelId,
          usedFallback,
          promptPreview: promptText,
        });
        resolutionLogged = true;
      };
      const tryOnce = async (modelId: string): Promise<string> => {
        const body = { ...baseBody, model: modelId };
        const res = await this.machineApi.streamPostJson('/api/orchestrator/run/stream', body, {
          signal: abort.signal,
        });

        if (!res.ok) {
          const errText = await res.text().catch(() => '');
          const e: any = new Error(errText || `Request failed (${res.status})`);
          e.status = res.status;
          e.errText = errText;
          throw e;
        }
        if (!res.body) {
          throw new Error('Streaming response body unavailable');
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();

        let sseBuffer = '';
        let fullText = '';

        const appendChunk = (chunkText: string) => {
          if (!chunkText) return;
          fullText += chunkText;
        };

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          sseBuffer += decoder.decode(value, { stream: true });

          // SSE events are separated by a blank line.
          while (true) {
            const sep = sseBuffer.indexOf('\n\n');
            if (sep === -1) break;

            const eventBlock = sseBuffer.slice(0, sep);
            sseBuffer = sseBuffer.slice(sep + 2);

            const lines = eventBlock.split('\n');
            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed.startsWith('data:')) continue;
              const data = trimmed.slice('data:'.length).trim();
              if (!data) continue;
              if (data === '[DONE]') continue;

              // Expect data to be JSON like: {"chunk":"..."}.
              try {
                const parsed = JSON.parse(data);
                const chunk = parsed && typeof parsed === 'object' ? (parsed as any).chunk : null;
                if (typeof chunk === 'string') {
                  appendChunk(chunk);
                } else if (typeof parsed === 'string') {
                  appendChunk(parsed);
                }
              } catch {
                // Fallback: treat the data as raw text.
                appendChunk(data);
              }
            }
          }
        }

        // Final decode flush (usually empty).
        const flush = decoder.decode();
        if (flush) {
          sseBuffer += flush;
        }

        return fullText;
      };

      let fullText = '';
      const modelsToTry = this.modelTryOrder();
      for (let i = 0; i < modelsToTry.length; i++) {
        const modelId = modelsToTry[i]!;
        try {
          fullText = await tryOnce(modelId);
          responseModelId = modelId;
          break;
        } catch (e: any) {
          if (abort.signal.aborted) throw e;
          const status = typeof e?.status === 'number' ? e.status : null;
          const errText = String(e?.errText ?? e?.message ?? '');
          const canRetry = this.isRateLimitError(status, errText) && i < modelsToTry.length - 1;
          if (!canRetry) throw e;
          // Keep UI clean: stay on "Generating…" while we retry silently.
          continue;
        }
      }

      const trimmed = fullText.trim();
      const parseDebugEnabled = this.isChatParseDebugEnabled();

      const relaxJson = (raw: string): string => String(raw ?? '').replace(/,\s*([}\]])/g, '$1').trim();

      const tryParseJsonObject = (raw: string): any | null => {
        const t = String(raw ?? '').trim();
        if (!t) return null;
        try {
          const parsed = JSON.parse(t);
          return parsed && typeof parsed === 'object' ? parsed : null;
        } catch {}
        try {
          const parsed = JSON.parse(relaxJson(t));
          return parsed && typeof parsed === 'object' ? parsed : null;
        } catch {}
        return null;
      };

      const extractJsonCandidates = (raw: string): string[] => {
        const t = String(raw ?? '');
        if (!t.trim()) return [];
        const out: string[] = [];

        // Grab JSON code fences even if surrounded by text.
        const fenceRegex = /```(?:json)?\s*([\s\S]*?)\s*```/gi;
        let m: RegExpExecArray | null = null;
        while ((m = fenceRegex.exec(t)) !== null) {
          if (m[1]) out.push(String(m[1]).trim());
        }

        // Extract balanced top-level objects from mixed text.
        let depth = 0;
        let start = -1;
        let inString = false;
        let escaped = false;
        for (let i = 0; i < t.length; i++) {
          const ch = t[i];
          if (inString) {
            if (escaped) {
              escaped = false;
            } else if (ch === '\\') {
              escaped = true;
            } else if (ch === '"') {
              inString = false;
            }
            continue;
          }
          if (ch === '"') {
            inString = true;
            continue;
          }
          if (ch === '{') {
            if (depth === 0) start = i;
            depth++;
            continue;
          }
          if (ch === '}') {
            if (depth > 0) depth--;
            if (depth === 0 && start >= 0) {
              out.push(t.slice(start, i + 1).trim());
              start = -1;
            }
          }
        }

        // Last fallback: outer-most object span.
        const firstObj = t.indexOf('{');
        const lastObj = t.lastIndexOf('}');
        if (firstObj !== -1 && lastObj !== -1 && lastObj > firstObj) {
          out.push(t.slice(firstObj, lastObj + 1).trim());
        }

        return out.filter(Boolean);
      };

      const tryEmitParsed = (parsed: any): boolean => {
        if (!parsed || typeof parsed !== 'object') return false;
        if (responseModelId) {
          this.lastUsedModelId = responseModelId;
          this.lastUsedModelWasFallback = this.normalizeModelId(responseModelId) !== this.normalizeModelId(requestedModelId);
        }
        logResolutionIfNeeded();
        const message = String((parsed as any).message ?? '');
        const patches = Array.isArray((parsed as any).patches) ? (parsed as any).patches : [];
        if (message) {
          assistantMsg.text = message;
          this.messageCreated.emit(assistantMsg);
          this.scrollToBottom();
        } else if (patches.length > 0) {
          // Some model/tool paths may return only patches with no user-facing message.
          assistantMsg.text = 'Done.';
          this.messageCreated.emit(assistantMsg);
          this.scrollToBottom();
        }
        this.messagesChange.emit(this.messages);
        this.aiResult.emit({ page, message, patches, raw: parsed });
        return true;
      };

      const directParsed = tryParseJsonObject(trimmed);
      if (tryEmitParsed(directParsed)) return;

      const candidates = extractJsonCandidates(trimmed);
      for (let i = candidates.length - 1; i >= 0; i--) {
        const parsed = tryParseJsonObject(candidates[i] ?? '');
        if (!parsed || typeof parsed !== 'object') continue;
        const hasShape = Object.prototype.hasOwnProperty.call(parsed, 'patches') || Object.prototype.hasOwnProperty.call(parsed, 'message');
        if (hasShape && tryEmitParsed(parsed)) return;
      }
      for (let i = candidates.length - 1; i >= 0; i--) {
        const parsed = tryParseJsonObject(candidates[i] ?? '');
        if (tryEmitParsed(parsed)) return;
      }

      // Fallback: if streaming output can't be parsed, retry once with the non-stream
      // endpoint which already returns an object shape ({ page, message, patches, raw }).
      try {
        const modelsToTry = this.modelTryOrder();
        for (let i = 0; i < modelsToTry.length; i++) {
          const modelId = modelsToTry[i]!;
          try {
            const body = { ...baseBody, model: modelId };
            const fallback = await this.machineApi.postJson<any>('/api/orchestrator/run', body, {
              signal: abort.signal,
            });
            responseModelId = modelId;
            if (tryEmitParsed(fallback)) return;
            if (fallback && typeof fallback === 'object') {
              // The API may wrap payloads as { success, data }.
              const unwrapped = (fallback as any).data;
              if (tryEmitParsed(unwrapped)) return;

              const rawDirect = (fallback as any).raw;
              if (tryEmitParsed(rawDirect)) return;

              const rawUnwrapped = (unwrapped as any)?.raw;
              if (tryEmitParsed(rawUnwrapped)) return;
            }
            break;
          } catch (e: any) {
            if (abort.signal.aborted) throw e;
            const status = typeof e?.status === 'number' ? e.status : null;
            const errText = String(e?.errText ?? e?.message ?? '');
            const canRetry = this.isRateLimitError(status, errText) && i < modelsToTry.length - 1;
            if (!canRetry) break;
          }
        }
      } catch {
        // Keep the existing user-facing parse failure below.
      }

      if (parseDebugEnabled) {
        const head = trimmed.slice(0, 500);
        const tail = trimmed.slice(Math.max(0, trimmed.length - 500));
        console.warn('[ChatComponent] parse failure', {
          page,
          fullTextLength: trimmed.length,
          candidates: candidates.length,
          head,
          tail,
        });
      }

      // If we got here, the model didn't return the expected JSON shape.
      // Keep the UI clean: do not display raw JSON blobs to the user.
      assistantMsg.text = 'AI returned an unexpected response. Please try again.';
      this.messageCreated.emit(assistantMsg);
      this.messagesChange.emit(this.messages);
      this.scrollToBottom();
    } catch (err: any) {
      if (abort.signal.aborted) {
        // Stop requested by user.
        return;
      }
      const msg = err?.message ?? 'Failed to generate response';
      this.logAgentEvent('error', {
        page,
        requestedModel: requestedModelId,
        promptPreview: promptText,
        error: msg,
      });
      assistantMsg.text = `Error: ${msg}`;
      this.messageCreated.emit(assistantMsg);
      this.messagesChange.emit(this.messages);
      this.scrollToBottom();
    } finally {
      this.isStreaming = false;
      this.inFlightAbort = undefined;
    }
  }

  public openContextModal() {
    // Blur the trigger to avoid focused element being inside aria-hidden subtree
    try { (document.activeElement as HTMLElement | null)?.blur?.(); } catch {}
    const modalRef = this.modalService.showChatContextModal(this.context);
    modalRef.result.then(
      (updated: any) => {
        if (updated) {
          this.context = updated;
        }
      },
      () => {}
    );
  }

  public onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (files.length === 0) return;
    this.attachedFiles.push(...files);
    this.toast.info(`${files.length} file(s) attached`);
    input.value = '';
  }

  public onVoiceInputToggle() {
    this.isListening = !this.isListening;
    this.toast.info(this.isListening ? 'Voice input: listening...' : 'Voice input: stopped');
  }

  public onRegenerate() {
    const lastUser = [...this.messages].reverse().find((m) => m.role === 'user');
    if (!lastUser) return;
    const promptText = String(lastUser.text ?? '').trim();
    if (!promptText) {
      this.toast.warning('There is no previous user message to regenerate');
      return;
    }
    void this.sendPrompt(promptText);
  }

  public onStop() {
    if (!this.isStreaming || !this.inFlightAbort) return;
    this.inFlightAbort.abort();
    this.inFlightAbort = undefined;
    this.isStreaming = false;
    this.toast.info('Generation stopped');
  }

  public onClear() {
    // If a generation is in progress, stop it before clearing.
    try {
      this.inFlightAbort?.abort();
    } catch {}
    this.inFlightAbort = undefined;
    this.isStreaming = false;
    this.messages = [];
    this.currentPrompt = '';
    this.resetPromptInputHeight();
    this.messagesChange.emit(this.messages);
    this.toast.success('Conversation cleared');
  }

  public onInputChange(evt: Event) {
    const target = evt.target as HTMLInputElement | HTMLTextAreaElement;
    this.currentPrompt = target.value ?? '';
    if (target instanceof HTMLTextAreaElement) {
      this.adjustPromptInputHeight(target);
    }
  }

  private focusPromptInput(): void {
    this.promptInput?.nativeElement.focus();
  }

  private resetPromptInputHeight(): void {
    const el = this.promptInput?.nativeElement;
    if (!el) {
      this.isPromptExpanded = false;
      return;
    }
    el.style.height = '';
    this.adjustPromptInputHeight(el);
  }

  private adjustPromptInputHeight(target?: HTMLTextAreaElement): void {
    const el = target ?? this.promptInput?.nativeElement;
    if (!el) {
      this.isPromptExpanded = false;
      return;
    }
    el.style.height = 'auto';
    const computed = window.getComputedStyle(el);
    const minHeight = Number.parseFloat(computed.minHeight);
    const maxHeight = Number.parseFloat(computed.maxHeight);
    const cappedHeight = Number.isFinite(maxHeight) ? Math.min(el.scrollHeight, maxHeight) : el.scrollHeight;
    el.style.height = `${cappedHeight}px`;
    const expandedThreshold = Number.isFinite(minHeight) ? minHeight + 2 : 50;
    this.isPromptExpanded = cappedHeight > expandedThreshold;
  }

  private scrollToBottom() {
    const el = this.messagesContainer?.nativeElement;
    if (!el) return;
    // Wait for DOM to update (new message rendered) before measuring
    requestAnimationFrame(() => {
      const maxScrollTop = el.scrollHeight - el.clientHeight;
      el.scrollTop = maxScrollTop < 0 ? 0 : maxScrollTop;
    });
  }
}
